import { SyntheticEvent } from 'react';

import { GravityType } from '@bitpatty/imgproxy-url-builder';
import { ImageLoaderProps } from 'next/image';
import { twMerge } from 'tailwind-merge';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { getImgProxyLoader } from '@lib/images/imgProxyLoader';
import { getSpeciesUrl } from '@domain/routes/urls';
import { EventMykissCardClicked } from '@generated/events';
import { Heading } from '../../Heading/Heading';
import { FishCatchesIcon } from '../../Icons/FishCatchesIcon';
import { NextImageWrapper } from '../../NextImage/NextImageWrapper';
import { LinkCard } from '../LinkCard/LinkCard';

export interface ISpeciesCardProps {
  name: string;
  numberCaught?: number;
  speciesId: string;
  /* An identifier for where this carousel is being used, e.g. species-top-waters */
  clickEventSource: EventMykissCardClicked['properties']['source'];
  /* An ID of the resource type where this carousel is being used, e.g. a species ID if it's on a species page */
  clickEventSourceId?: EventMykissCardClicked['properties']['source_id'];
  /* The position of the card in a list (if applicable) */
  clickEventPosition?: EventMykissCardClicked['properties']['position'];
  className?: string;
  imageClassName?: string;
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  onClick?: (event: SyntheticEvent) => void;
}

export const SpeciesCard = ({
  speciesId,
  name,
  clickEventSource,
  numberCaught,
  clickEventPosition,
  clickEventSourceId,
  className = '',
  imageClassName = '',
  imageUrl,
  imageWidth,
  imageHeight,
  onClick,
}: ISpeciesCardProps) => {
  const { currentPage } = useAnalytics();

  const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getImgProxyLoader(src, width, imageWidth, imageHeight, {
      quality,
      gravityType: GravityType.CENTER,
    });

  return (
    <LinkCard
      onClick={onClick}
      to={getSpeciesUrl(speciesId, name)}
      eventProperties={{
        source: clickEventSource,
        target: 'species',
        target_id: speciesId,
        position: clickEventPosition,
        source_id: clickEventSourceId,
        page: currentPage,
      }}
      className={twMerge('rounded-md md:rounded-lg', className)}
    >
      {imageUrl && imageWidth && imageHeight ? (
        <NextImageWrapper
          wrapperClassName="mb-3 group-hover:shadow-md"
          imageClassName={twMerge(
            'size-full rounded-md md:rounded-lg object-cover',
            imageClassName,
          )}
          customPlaceholderIcon={<FishCatchesIcon size={93} />}
          alt={name}
          src={imageUrl}
          loader={imgProxyLoader}
          width={imageWidth}
          height={imageHeight}
          sizes="(min-width: 1680px) 376px, (min-width: 990px) 25vw, (min-width: 600px) 83vw, 66vw"
        />
      ) : (
        <div className="bg-twilight text-lake-fog mb-3 flex aspect-10/7 w-full items-center justify-center rounded-md md:rounded-lg">
          <FishCatchesIcon size={93} />
        </div>
      )}

      <Heading as="div" variant="xs" className="mb-1 lg:mb-1.5">
        {name}
      </Heading>
      {numberCaught && (
        <div className="text-trench mb-1" style={{ fontSize: '13px' }}>
          <span className="font-semibold">{numberCaught}</span>
          {numberCaught === 1
            ? ` ${name} has been caught in this region`
            : ` ${name} have been caught in this region`}
        </div>
      )}
    </LinkCard>
  );
};
